<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {usePage} from "@inertiajs/vue3";
  import {MedButton, MedLink, MedSpinner} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "LayoutFooter",
  });

  const page = usePage();

  const refDialog = ref(null);
  const medicalCertification = ref(false);
  const manuals = ref(null);

  const environment = computed(() => page.props.environmentConfig?.environment);
  const isDevelop = computed(() => page.props.environmentConfig?.isDevelop && medicalCertification.value === false);
  const showCeLogo = computed(() => !page.props.can?.is_education);
  const udi = computed(() => page.props.udi);
  const version = computed(() => page.props.environmentConfig?.version);

  const license = computed(() => page.props.client?.license);

  const productName = computed(() => {
    return license.value?.product_name ?? "MediCheck Online";
  });

  const versionName = computed(() => {
    return license.value?.version_name ? license.value?.version_name + "-Version" : "Version";
  });

  const licenseName = computed(() => {
    return license.value?.license_name;
  });

  const distributorLabel = computed(() => {
    return license.value?.is_connect ? "Vertreiber/Händler und Lizenzgeber" : "Betreiber";
  });

  const enableMedicalCertification = () => {
    medicalCertification.value = true;

    globalThis.setTimeout(() => {
      medicalCertification.value = false;
    }, 15_000);
  };

  const openLabellingDialog = () => {
    if (refDialog.value) {
      refDialog.value.open();
    }

    if (manuals.value === null) {
      Promise.all([
        useAuthHttp().get(route("url-redirect", {identifier: "manual-medicheck"})),
        useAuthHttp().get(route("url-redirect", {identifier: "manual-medicheck-wawi"})),
      ])
        .then((responses) => {
          manuals.value = {
            online: responses[0],
            connect: responses[1],
          };
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };
</script>

<template>
  <footer class="bottom-0 flex w-full bg-white shadow-[0_10px_15px_8px_rgba(0,0,0,0.3)]">
    <div class="mx-auto flex max-w-11xl flex-1 flex-wrap justify-between gap-2 px-2 py-3 sm:flex-row sm:gap-0 md:px-4">
      <div class="flex items-center">
        <template v-if="isDevelop">
          <MedButton variant="dev" data-test="footer-show-ce-logo-button" @click="enableMedicalCertification">
            Dev / Test System ( {{ environment }} )
          </MedButton>
        </template>

        <template v-else>
          <template v-if="showCeLogo">
            <div class="flex items-center" data-test="footer-ce-logo">
              <img alt="CeLogo" src="/resources/assets/images/ce-with-number.png" class="mt-1.5 mr-1 ml-2 h-5" />

              <span class="text-gray-500">
                {{ productName }}
              </span>

              <img
                alt="MedicalDeviceSymbol"
                src="/resources/assets/images/MedicalDeviceSymbol-20221027-121211.png"
                class="mx-0.5 h-6"
              />
            </div>
          </template>
        </template>
      </div>

      <div class="flex items-center">
        <MedLink
          variant="primary"
          href="https://www.abda.de/fuer-apotheker/arzneimittelkommission/berichtsbogen-formulare"
          target="_blank"
          class="text-sm md:text-base"
          data-test="footer-uaw-amk-link"
        >
          UAW an AMK melden
        </MedLink>

        <div class="mx-2 h-5 w-px bg-gray-400" />

        <MedLink
          as="button"
          variant="primary"
          data-test="footer-labeling-link"
          class="text-sm md:text-base"
          @click="openLabellingDialog"
        >
          Kennzeichnung
        </MedLink>

        <div class="mx-2 h-5 w-px bg-gray-400" />

        <MedLink
          variant="primary"
          href="https://www.pharma4u.de/allgemein/impressum"
          target="_blank"
          class="text-sm md:text-base"
          data-test="footer-imprint-link"
        >
          Impressum
        </MedLink>
      </div>

      <div class="flex items-center">
        <span class="pr-1 text-sm text-gray-500 md:text-base">
          {{ versionName }}
          {{ version }}
        </span>
      </div>
    </div>

    <ComponentDialog
      ref="refDialog"
      dialog-title="Kennzeichnung"
      dialog-type="information"
      dialog-width="medium"
      test-id="footer-labeling"
    >
      <template #content>
        <h4 class="text-md leading-6 font-medium">{{ distributorLabel }}</h4>

        <div class="mt-1 flex">
          <p>
            pharma4u GmbH
            <br />
            Apothekerhaus
            <br />
            Carl-Mannich-Straße 26
            <br />
            65760 Eschborn
          </p>
        </div>

        <h4 class="text-md mt-8 leading-6 font-medium">Hersteller und UDI</h4>

        <div class="flex flex-col gap-8 sm:flex-row">
          <div class="mt-2 flex">
            <img alt="iso" class="mt-2 h-16" src="/resources/assets/images/iso_grs_7000_3082.png" />

            <p class="ml-3 self-center">
              Johner Medical GmbH,
              <br />
              Niddastr. 91,
              <br />
              60329 Frankfurt
            </p>
          </div>

          <div class="flex">
            <img
              alt="MedicalDeviceSymbol"
              class="-ml-1 h-16"
              src="/resources/assets/images/MedicalDeviceSymbol-20221027-121211.png"
            />

            <p class="mt-2 ml-3">
              UDI:
              <span>{{ udi }}</span>
            </p>
          </div>
        </div>

        <div v-if="manuals !== null" class="mt-8">
          <p v-if="licenseName === null || licenseName === 'online-pro' || licenseName === 'connect-pro'">
            <MedLink
              target="_blank"
              variant="primary"
              :href="manuals.online.target_url"
              data-test="footer-labeling-dialog-manuels-online-link"
            >
              {{ manuals.online.link_text }}
            </MedLink>
          </p>

          <p v-if="licenseName === 'connect-core' || licenseName === 'connect-pro'">
            <MedLink
              target="_blank"
              variant="primary"
              :href="manuals.connect.target_url"
              data-test="footer-labeling-dialog-manuels-connect-link"
            >
              {{ manuals.connect.link_text }}
            </MedLink>
          </p>
        </div>

        <div v-else class="mt-8">
          <MedSpinner size="xl" data-test="footer-loading-spinner" />
        </div>

        <h4 class="text-md mt-8 leading-6 font-medium">Meldungen von Vorkommnissen MediCheck</h4>

        <div class="mt-1 flex">
          <p>
            Fehler oder Vorkommnisse, die in Zusammenhang mit der Nutzung der Software MediCheck auftreten, melden Sie
            bitte an die Adresse
            <MedLink
              href="mailto:meldung@wir-machen-amts.de"
              data-test="footer-labeling-dialog-email-amts-link"
              variant="primary"
            >
              meldung@wir-machen-amts.de
            </MedLink>
            .
          </p>
        </div>
      </template>
    </ComponentDialog>
  </footer>
</template>
